import React, { useEffect, useState, useContext } from 'react'
import { Card, Button, Row, Col } from 'react-bootstrap'
import { useToasts } from 'react-toast-notifications'
import { AppContext } from '../../../app'
import { UsersList } from './UsersList'
import { InviteUserModal } from './InviteUserModal'
import { EditUserRoleModal } from './EditUserRoleModal'
import { UserWithRole } from '../../../domain/usecases/UserRoles'
import { Role } from '../../../domain/usecases/Roles'
import { RemoteGetCompanyUsers } from '../../../data/usecases/UserRoles/RemoteGetCompanyUsers'
import { RemoteRemoveUserFromCompany } from '../../../data/usecases/UserRoles/RemoteRemoveUserFromCompany'
import { RemoteGetRoles } from '../../../data/usecases/Roles/RemoteGetRoles'
import { useAxiosHttpClient } from '../../../hooks/useAxiosHttpClient'
import { URL_API } from '../../../services/api_url'
import { usePermissions } from '../../../hooks/usePermissions'

export const UserManagementPage: React.FC = () => {
  const { user } = useContext(AppContext)
  const { addToast } = useToasts()
  const httpClient = useAxiosHttpClient()
  const { can } = usePermissions()

  const [users, setUsers] = useState<UserWithRole[]>([])
  const [roles, setRoles] = useState<Role[]>([])
  const [loading, setLoading] = useState(true)
  const [showInviteModal, setShowInviteModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [selectedUser, setSelectedUser] = useState<UserWithRole | null>(null)

  const companyId = user?.selectedContext?.companyId

  useEffect(() => {
    if (companyId) {
      loadData()
    }
  }, [companyId])

  const loadData = async () => {
    if (!companyId) return

    setLoading(true)
    try {
      // Load users and roles in parallel
      const getUsersUseCase = new RemoteGetCompanyUsers(
        URL_API || '',
        httpClient
      )
      const getRolesUseCase = new RemoteGetRoles(URL_API || '', httpClient)

      const [usersData, rolesData] = await Promise.all([
        getUsersUseCase.getUsers(companyId),
        getRolesUseCase.getRoles(companyId)
      ])

      setUsers(usersData)
      setRoles(rolesData)
    } catch (error) {
      console.error('Error loading data:', error)
      addToast('Erro ao carregar usuários e funções', {
        appearance: 'error',
        autoDismiss: true
      })
    } finally {
      setLoading(false)
    }
  }

  const handleInviteSuccess = () => {
    setShowInviteModal(false)
    loadData()
    addToast('Usuário convidado com sucesso!', {
      appearance: 'success',
      autoDismiss: true
    })
  }

  const handleEditUser = (user: UserWithRole) => {
    setSelectedUser(user)
    setShowEditModal(true)
  }

  const handleEditSuccess = () => {
    setShowEditModal(false)
    setSelectedUser(null)
    loadData()
    addToast('Função do usuário atualizada com sucesso!', {
      appearance: 'success',
      autoDismiss: true
    })
  }

  const handleRemoveUser = async (userId: string) => {
    if (!companyId) return

    if (
      !window.confirm('Tem certeza que deseja remover este usuário da empresa?')
    ) {
      return
    }

    try {
      const removeUseCase = new RemoteRemoveUserFromCompany(
        URL_API || '',
        httpClient
      )
      await removeUseCase.remove(userId, companyId)

      addToast('Usuário removido com sucesso!', {
        appearance: 'success',
        autoDismiss: true
      })

      loadData()
    } catch (error) {
      console.error('Error removing user:', error)
      addToast(error?.message || 'Erro ao remover usuário', {
        appearance: 'error',
        autoDismiss: true
      })
    }
  }

  if (!companyId) {
    return (
      <Card>
        <Card.Body>
          <p>Por favor, selecione uma empresa no menu superior.</p>
        </Card.Body>
      </Card>
    )
  }

  return (
    <>
      <Row>
        <Col>
          <Card>
            <Card.Header>
              <div className="d-flex justify-content-between align-items-center">
                <h5>Gestão de Usuários</h5>
                {can.create('users') && (
                  <Button
                    variant="primary"
                    onClick={() => setShowInviteModal(true)}
                  >
                    <i className="feather icon-user-plus mr-2" />
                    Convidar Usuário
                  </Button>
                )}
              </div>
            </Card.Header>
            <Card.Body>
              {loading ? (
                <div className="text-center py-5">
                  <div className="spinner-border" role="status">
                    <span className="sr-only">Carregando...</span>
                  </div>
                </div>
              ) : (
                <UsersList
                  users={users}
                  roles={roles}
                  onEdit={can.update('users') ? handleEditUser : undefined}
                  onRemove={can.delete('users') ? handleRemoveUser : undefined}
                />
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {showInviteModal && (
        <InviteUserModal
          show={showInviteModal}
          onHide={() => setShowInviteModal(false)}
          onSuccess={handleInviteSuccess}
          roles={roles}
          companyId={companyId}
        />
      )}

      {showEditModal && selectedUser && (
        <EditUserRoleModal
          show={showEditModal}
          onHide={() => {
            setShowEditModal(false)
            setSelectedUser(null)
          }}
          onSuccess={handleEditSuccess}
          user={selectedUser}
          roles={roles}
          companyId={companyId}
        />
      )}
    </>
  )
}

export default UserManagementPage
